import { IGenericSelectDataModel } from "@kuika/kuika-cl-model";
import Editor, { BeforeMount, OnMount } from "@monaco-editor/react";
import * as monaco from "monaco-editor";
import * as React from "react";
import { codeEditorTokenRules } from "../../../code-editor-shell/code-editor-theme";

export interface ICodeViewComponentProps {
  datasourceId: string;
  selectData: IGenericSelectDataModel;
  sqlCode: string;
}

export interface ICodeViewComponentState {
  wrapStatus: string;
}

// Monaco cannot read CSS variables, so the theme is re-declared whenever the app
// theme flips and its surface is pulled from the live token instead of a literal.
const EDITOR_THEME: string = "kuika-sql";
const SURFACE_TOKEN: string = "--dark-2";
const SURFACE_FALLBACK: Record<"light" | "dark", string> = { light: "#f1f2f4", dark: "#353a40" };

const resolveAppTheme = (): "light" | "dark" =>
  document.documentElement.getAttribute("data-theme") === "dark" ? "dark" : "light";

const readSurface = (theme: "light" | "dark"): string => {
  const token: string = getComputedStyle(document.documentElement).getPropertyValue(SURFACE_TOKEN).trim();
  return token || SURFACE_FALLBACK[theme];
};

export class CodeViewComponent extends React.PureComponent<ICodeViewComponentProps, ICodeViewComponentState> {
  editorRef: monaco.editor.IStandaloneCodeEditor | null = null;
  monacoRef: typeof monaco | null = null;
  themeObserver: MutationObserver | null = null;

  constructor(props: ICodeViewComponentProps) {
    super(props);
    this.state = {
      wrapStatus: "on"
    };
  }

  componentDidMount(): void {
    this.themeObserver = new MutationObserver(this.applyEditorTheme);
    this.themeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] });
  }

  componentWillUnmount(): void {
    this.themeObserver?.disconnect();
    this.themeObserver = null;
  }

  applyEditorTheme = (): void => {
    const monacoApi: typeof monaco | null = this.monacoRef;
    if (!monacoApi) return;

    const theme: "light" | "dark" = resolveAppTheme();
    const surface: string = readSurface(theme);

    // `vs` / `vs-dark` carry the syntax palette, minus the SQL colours they get wrong;
    // only the chrome follows the token.
    monacoApi.editor.defineTheme(EDITOR_THEME, {
      base: theme === "dark" ? "vs-dark" : "vs",
      inherit: true,
      rules: codeEditorTokenRules(theme),
      colors: {
        "editor.background": surface,
        "editorGutter.background": surface
      }
    });
    monacoApi.editor.setTheme(EDITOR_THEME);
  };

  handleEditorMount: OnMount = (editor) => {
    this.editorRef = editor as unknown as monaco.editor.IStandaloneCodeEditor;
  };

  handleBeforeMount: BeforeMount = (monacoApi) => {
    this.monacoRef = monacoApi as unknown as typeof monaco;
    this.applyEditorTheme();
  };

  render() {
    return (
      <div
        className="code-view-container"
        style={{
          height: "400px",
          minHeight: "400px",
          background: "var(--dark-2)",
          borderRadius: "var(--radius-l)",
          overflow: "hidden"
        }}
      >
        <Editor
          height="100%"
          language="sql"
          theme={EDITOR_THEME}
          value={this.props.sqlCode}
          options={{
            readOnly: true,
            selectOnLineNumbers: true,
            wordWrap: this.state.wrapStatus as "on" | "off",
            scrollbar: {
              alwaysConsumeMouseWheel: false
            },
            minimap: {
              enabled: false
            },
            fontSize: 11,
            lineNumbers: "on",
            // Read-only preview: there is no caret, so the active-line band was
            // just a stray stripe across the gutter and code.
            renderLineHighlight: "none",
            automaticLayout: true
          }}
          beforeMount={this.handleBeforeMount}
          onMount={this.handleEditorMount}
        />
      </div>
    );
  }
}
